import { buttonStyle } from '@florencelabs/button';
import clsx from 'clsx';
import { addDays } from 'date-fns';
import React, { forwardRef, PropsWithChildren, ReactNode } from 'react';
import { Label, Radio, RadioGroupProps } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { RovingFocusRadioGroup } from '@provider-directory/components/forms/roving-focus-radio-group';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';
import { formatDateString } from '@provider-directory/functions/date';
import { tzFreeDate } from '@provider-directory/functions/date';
import { DaySlot, generateEmptySlots } from '../availability.model';

export interface DaySlotsPickerProps extends RadioGroupProps {
  daySlots: DaySlot[];
  isLoading?: boolean;
  startDate?: Date;
  showGap?: boolean;
  endDate?: Date | null;
  rows?: number;
  providerName?: string;
  noneAfterDateSlot?: ReactNode;
}

const findDisplaySlotsSearch = (
  daySlots: DaySlot[],
  startDate: Date,
  endDate: Date,
): {
  results: DaySlot[];
  nextAvailableDate: string | null;
} => {
  const fullRange = generateEmptySlots(startDate, endDate);
  const results = fullRange.map((slot) => {
    const matchingSlot = daySlots.find((s) => s.date === slot.date);
    return matchingSlot ?? slot;
  });
  const allEmpty = results.every((slot) => slot.slotsNumber === 0);
  let nextAvailableDate: string | null = null;

  if (allEmpty && daySlots.length > 0) {
    // Look for the next slot after endDate
    const nextSlotAfterEndDate = daySlots.find(
      (slot) => new Date(slot.date) > endDate,
    );

    if (nextSlotAfterEndDate) {
      // If a slot exists after endDate, set it as the next available date
      nextAvailableDate = nextSlotAfterEndDate.date;
      return { results: [nextSlotAfterEndDate], nextAvailableDate };
    }

    // If no slot exists after endDate, fallback to the last slot in daySlots
    const lastSlot = daySlots[daySlots.length - 1];
    nextAvailableDate = lastSlot.date;
    return { results: [lastSlot], nextAvailableDate };
  }

  return { results, nextAvailableDate };
};

const LoadingSkeleton = ({ rows }: { rows: number }) => {
  return new Array(rows * 4)
    .fill(0)
    .map((_, i) => (
      <LoadingGradient className={'h-full w-full rounded-sm'} key={i} />
    ));
};

const GapButton = ({
  dateInFuture,
  onSelect,
}: {
  dateInFuture: string;
  onSelect?: (date: string) => void;
}) => {
  const { t } = useTranslation();
  const ariaLabel = t('availability.nextAvailable.ariaLabel', {
    date: formatDateString(dateInFuture, 'EEEE, MMMM d'),
  });

  return (
    <div className={'col-span-full flex w-full flex-col justify-center'}>
      <Radio
        onClick={() => {
          onSelect?.(dateInFuture);
        }}
        aria-label={ariaLabel}
        className={(renderProps) =>
          clsx(
            'h-[75px] w-full flex-col !rounded-sm !p-3',
            'outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
            buttonStyle({
              variant: 'clientTonal',
              isFocusVisible: renderProps.isFocusVisible,
            }),
          )
        }
        value={dateInFuture}
      >
        <span className={'text-body-xsmall'}>{t('search.nextAvailable')}</span>
        <span className={'text-body-small font-medium'}>
          {formatDateString(dateInFuture, 'EEE, MMM d')}
        </span>
      </Radio>
    </div>
  );
};

const DayButton = ({
  isDisabled,
  date,
  slotsNumber,
  onSelect,
}: {
  isDisabled: boolean;
  date: string;
  slotsNumber: number;
  onSelect?: (date: string) => void;
}) => {
  const { t } = useTranslation();
  const formattedDate = formatDateString(date, 'EEEE, MMMM d');

  let ariaLabel: string;
  if (slotsNumber === 0) {
    ariaLabel = t('availability.dayButton.ariaLabelNone', {
      date: formattedDate,
    });
  } else {
    ariaLabel = t('availability.dayButton.ariaLabel', {
      date: formattedDate,
      count: slotsNumber,
    });
  }

  return (
    <Radio
      onClick={() => {
        if (!isDisabled) {
          onSelect?.(date);
        }
      }}
      aria-label={ariaLabel}
      isDisabled={isDisabled}
      data-testid={'day-buttons'}
      className={(renderProps) =>
        clsx(
          'flex h-full shrink-0 flex-col !rounded-sm !p-3',
          'outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
          isDisabled &&
            'cursor-not-allowed !bg-primary-50 [&[data-disabled]_span]:opacity-25',
          buttonStyle({
            variant: 'clientTonal',
            size: 'small',
            isFocusVisible: renderProps.isFocusVisible,
          }),
        )
      }
      key={date}
      value={date}
    >
      <span data-testid={'availability-days'} className={'text-body-xsmall'}>
        {formatDateString(date, 'EEE')}
      </span>
      <span
        data-testid={'availability-dates'}
        className={'mb-3 text-body-small font-medium'}
      >
        {formatDateString(date, 'MMM d')}
      </span>
      <span
        data-testid={'availability-appts'}
        className={'text-body-xsmall opacity-60'}
      >
        {slotsNumber} appts
      </span>
    </Radio>
  );
};

const NoAvailabilityAfterDate = () => {
  const { t } = useTranslation();

  return (
    <div
      className={
        'col-span-4 row-span-full my-auto flex w-full flex-col gap-4 rounded-sm bg-black/5 p-4 text-body-small text-neutral-600'
      }
    >
      {t('availability.noneAfterDate')}
    </div>
  );
};

interface ButtonGridContainerProps extends PropsWithChildren {
  rows: number;
}

const ButtonGridContainer = ({ children, rows }: ButtonGridContainerProps) => (
  <div
    className={'grid w-full grid-cols-4 gap-2'}
    style={{
      gridTemplateRows: `repeat(${rows}, 97px)`, // we have a fixed row height
    }}
  >
    {children}
  </div>
);

export const DaySlotsPicker = forwardRef<HTMLDivElement, DaySlotsPickerProps>(
  (
    {
      daySlots,
      isLoading,
      startDate = tzFreeDate(),
      rows = 2,
      endDate,
      onChange,
      providerName,
      noneAfterDateSlot,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    let gapDate: string | null = null;
    const { results: displaySlots, nextAvailableDate } = findDisplaySlotsSearch(
      daySlots,
      startDate,
      endDate ?? addDays(startDate, rows * 4 - 1),
    );

    if (nextAvailableDate) {
      // when the startDate there is not next AvailableSlots in the future
      // so we get last one and show only next avaible date for this provider
      gapDate = displaySlots[0].date;
    }

    const showNoneAfterDateSlot =
      !isLoading && !!gapDate && tzFreeDate(gapDate) <= startDate;

    if (showNoneAfterDateSlot) {
      return (
        <div
          data-testid={'availability-day-picker'}
          className={'flex flex-wrap gap-2'}
        >
          {noneAfterDateSlot ?? <NoAvailabilityAfterDate />}
        </div>
      );
    }

    return (
      <RovingFocusRadioGroup ref={ref} {...props}>
        <Label className={'sr-only'}>
          {providerName
            ? t('appointmentDates.labelWithProvider', { providerName })
            : t('appointmentDates.label')}
        </Label>
        <div
          data-testid={'availability-day-picker'}
          className={'flex flex-wrap gap-2'}
        >
          {isLoading ? (
            <ButtonGridContainer rows={rows}>
              <LoadingSkeleton
                data-testid={'date-slots-skeleton'}
                rows={rows}
              />
            </ButtonGridContainer>
          ) : !gapDate ? (
            <ButtonGridContainer rows={rows}>
              {displaySlots.map(({ date, slotsNumber }) => {
                const isDisabled = slotsNumber === 0;
                return (
                  <DayButton
                    key={date}
                    isDisabled={isDisabled}
                    date={date}
                    slotsNumber={slotsNumber}
                    onSelect={onChange}
                  />
                );
              })}
            </ButtonGridContainer>
          ) : (
            <div data-testid={'day-buttons'} className={`my-auto w-full`}>
              <GapButton dateInFuture={gapDate} onSelect={onChange} />
            </div>
          )}
        </div>
      </RovingFocusRadioGroup>
    );
  },
);

DaySlotsPicker.displayName = 'DaySlotsPicker';
